import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "../components/Layout";
import { Button } from "../components/ui/Button";
import api from "../services/api";
import { ArrowLeft, Download, Users } from "lucide-react";

// Dimensões dos nós e espaçamentos do organograma (em px).
const NODE_W = 200;
const NODE_H = 62;
const H_GAP = 28;
const V_GAP = 70;
const SLOT = NODE_W + H_GAP;
const LEVEL = NODE_H + V_GAP;
const PAD = 24;

const fullName = (c) => `${c.first_name || ""} ${c.last_name || ""}`.trim() || "—";
const trunc = (s, n) => (s && s.length > n ? `${s.slice(0, n - 1)}…` : s || "");

const FluxogramaColaboradores = () => {
  const navigate = useNavigate();
  const svgRef = useRef(null);
  const [people, setPeople] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let alive = true;
    api
      .get("/collaborators/listbox")
      .then((r) => {
        if (alive) setPeople(Array.isArray(r.data) ? r.data : []);
      })
      .catch((e) => {
        if (alive) setError(e.response?.data?.detail || "Erro ao carregar colaboradores.");
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, []);

  // Monta o layout do organograma (posições + arestas) a partir da hierarquia.
  const { nodes, edges, width, height } = useMemo(() => {
    const byId = new Map(people.map((p) => [p.id, p]));
    const childrenOf = new Map();
    const roots = [];
    people.forEach((p) => {
      const mid = p.manager_id;
      if (mid && byId.has(mid) && mid !== p.id) {
        if (!childrenOf.has(mid)) childrenOf.set(mid, []);
        childrenOf.get(mid).push(p);
      } else {
        roots.push(p);
      }
    });

    const pos = new Map(); // id -> {x(slot), depth}
    const visited = new Set();
    let cursor = 0;
    let maxDepth = 0;

    const place = (node, depth) => {
      if (visited.has(node.id)) {
        // proteção contra ciclo: trata como folha
        const x = cursor;
        cursor += 1;
        pos.set(node.id, { x, depth });
        return x;
      }
      visited.add(node.id);
      maxDepth = Math.max(maxDepth, depth);
      const kids = childrenOf.get(node.id) || [];
      if (kids.length === 0) {
        const x = cursor;
        cursor += 1;
        pos.set(node.id, { x, depth });
        return x;
      }
      const xs = kids.map((k) => place(k, depth + 1));
      const x = (xs[0] + xs[xs.length - 1]) / 2;
      pos.set(node.id, { x, depth });
      return x;
    };

    roots.forEach((r) => place(r, 0));

    const nodesOut = [];
    pos.forEach((p, id) => {
      const person = byId.get(id);
      if (!person) return;
      nodesOut.push({
        id,
        x: PAD + p.x * SLOT,
        y: PAD + p.depth * LEVEL,
        person,
      });
    });

    const nodeXY = new Map(nodesOut.map((n) => [n.id, n]));
    const edgesOut = [];
    people.forEach((p) => {
      const mid = p.manager_id;
      if (mid && nodeXY.has(mid) && nodeXY.has(p.id) && mid !== p.id) {
        const parent = nodeXY.get(mid);
        const child = nodeXY.get(p.id);
        const px = parent.x + NODE_W / 2;
        const py = parent.y + NODE_H;
        const cx = child.x + NODE_W / 2;
        const cy = child.y;
        const midY = (py + cy) / 2;
        edgesOut.push(`M ${px} ${py} V ${midY} H ${cx} V ${cy}`);
      }
    });

    const w = Math.max(cursor * SLOT, SLOT) + PAD * 2 - H_GAP;
    const h = (maxDepth + 1) * LEVEL + PAD * 2 - V_GAP + NODE_H;
    return { nodes: nodesOut, edges: edgesOut, width: w, height: h };
  }, [people]);

  const serializeSvg = () => {
    const el = svgRef.current;
    if (!el) return null;
    let source = new XMLSerializer().serializeToString(el);
    if (!/xmlns="http/.test(source)) {
      source = source.replace("<svg", '<svg xmlns="http://www.w3.org/2000/svg"');
    }
    return source;
  };

  const downloadSvg = () => {
    const source = serializeSvg();
    if (!source) return;
    const blob = new Blob([`<?xml version="1.0" encoding="UTF-8"?>\n${source}`], {
      type: "image/svg+xml;charset=utf-8",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "fluxograma-colaboradores.svg";
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const downloadPng = () => {
    const source = serializeSvg();
    if (!source) return;
    const svg64 = btoa(unescape(encodeURIComponent(source)));
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext("2d");
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, width, height);
      ctx.drawImage(img, 0, 0);
      canvas.toBlob((blob) => {
        if (!blob) return;
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "fluxograma-colaboradores.png";
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
      }, "image/png");
    };
    img.src = `data:image/svg+xml;base64,${svg64}`;
  };

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              onClick={() => navigate("/collaborators")}
              aria-label="Voltar"
              className="h-auto px-2 py-1.5 text-gray-600"
            >
              <ArrowLeft size={18} />
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Fluxograma de Colaboradores</h1>
              <p className="text-gray-500">Organograma montado pela hierarquia de gerentes.</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={downloadPng}
              disabled={nodes.length === 0}
              className="h-auto gap-2 px-3 py-2 text-gray-700"
            >
              <Download size={16} /> PNG
            </Button>
            <Button
              type="button"
              onClick={downloadSvg}
              disabled={nodes.length === 0}
              className="h-auto gap-2 px-3 py-2"
            >
              <Download size={16} /> Baixar (SVG)
            </Button>
          </div>
        </div>

        {error && (
          <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-red-700">{error}</div>
        )}

        <div className="rounded-lg border bg-white p-4">
          {loading ? (
            <div className="py-10 text-center text-gray-500">Carregando...</div>
          ) : nodes.length === 0 ? (
            <div className="py-10 text-center text-gray-500">
              <Users className="mx-auto mb-2 text-gray-300" size={28} />
              Nenhum colaborador ativo para montar o fluxograma.
            </div>
          ) : (
            <div className="overflow-auto">
              <svg
                ref={svgRef}
                width={width}
                height={height}
                viewBox={`0 0 ${width} ${height}`}
                style={{ background: "#ffffff", fontFamily: "Inter, system-ui, sans-serif" }}
              >
                {edges.map((d, i) => (
                  <path key={`e${i}`} d={d} fill="none" stroke="#94a3b8" strokeWidth="1.5" />
                ))}
                {nodes.map((n) => (
                  <g key={n.id}>
                    <rect
                      x={n.x}
                      y={n.y}
                      width={NODE_W}
                      height={NODE_H}
                      rx="10"
                      fill="#ffffff"
                      stroke="#2563eb"
                      strokeWidth="1.5"
                    />
                    <rect x={n.x} y={n.y} width="6" height={NODE_H} rx="3" fill="#2563eb" />
                    <text
                      x={n.x + 16}
                      y={n.y + 24}
                      fill="#111827"
                      fontSize="13"
                      fontWeight="700"
                    >
                      {trunc(fullName(n.person), 24)}
                    </text>
                    <text x={n.x + 16} y={n.y + 41} fill="#2563eb" fontSize="11">
                      {trunc(n.person.job_title || "—", 26)}
                    </text>
                    <text x={n.x + 16} y={n.y + 55} fill="#6b7280" fontSize="10">
                      {trunc(n.person.department || "", 30)}
                    </text>
                  </g>
                ))}
              </svg>
            </div>
          )}
        </div>

        <p className="text-xs text-gray-400">
          Defina o campo <strong>Gerente</strong> em cada colaborador para montar a hierarquia.
          Quem não tem gerente aparece no topo.
        </p>
      </div>
    </Layout>
  );
};

export default FluxogramaColaboradores;
